import React from "react";
import ReactDOMServer from "react-dom/server";
import {
  EmptyNote,
  InfoTable,
  SectionTitle,
  StatusBanner,
  SubTitle,
  formatDate,
  getPaidInstallmentCount,
  isFullyPaidFreeze,
  money,
} from "./BnplFreezePDFUtils";

export default function BnplFreezePDFPaymentStatic({ freeze, detail, flight }) {
  const repayments = freeze?.repayments || [];
  const madeRepayments = freeze?.madeRepayments || [];
  const fullyPaid = isFullyPaidFreeze(freeze, detail);
  const paidCount = getPaidInstallmentCount(
    repayments,
    madeRepayments,
    fullyPaid,
  );
  const adult = flight?.adult;
  const lastPayment = madeRepayments[madeRepayments.length - 1];

  const content = (
    <table className="w-full mb-2" cellPadding="0" cellSpacing="0">
      <tbody>
        <tr>
          <td>
            <SectionTitle>6. Flight Price</SectionTitle>
            <InfoTable
              rows={[
                {
                  label: "Base Fare",
                  value: adult?.basePrice || money(flight?.basePrice),
                },
                { label: "Taxes", value: adult?.taxes || "-" },
                { label: "Ticketing Fee", value: money(flight?.ticketingFee) },
                {
                  label: "Flight Customer Payable",
                  value: money(flight?.customerPayable),
                },
              ]}
            />

            <SectionTitle>7. BNPL Payment Plan</SectionTitle>
            <InfoTable
              rows={[
                { label: "Frozen Amount", value: money(freeze?.frozenAmount) },
                {
                  label: "Initial Payment Requirement",
                  value: freeze?.min != null ? `${freeze.min}%` : "-",
                },
                {
                  label: "Initial Payment",
                  value: money(freeze?.firstPayment),
                },
                {
                  label: "Amount Financed / Loaned",
                  value: money(freeze?.loanedAmount),
                },
                { label: "Service Fee", value: money(freeze?.serviceFee) },
                {
                  label: "Service Fee Rate",
                  value:
                    freeze?.interest != null ? `${freeze.interest}%` : "-",
                },
                {
                  label: "Plan",
                  value:
                    freeze?.plan != null
                      ? `${freeze.plan} repayment${
                          freeze.plan === 1 ? "" : "s"
                        }`
                      : "-",
                },
              ]}
            />

            <SectionTitle>8. Payment Status</SectionTitle>
            <StatusBanner tone={fullyPaid ? "success" : "warning"}>
              {fullyPaid ? "Payment completed" : "Payment in progress"}
            </StatusBanner>
            <InfoTable
              rows={[
                { label: "Total Repaid", value: money(detail?.alreadyRepaid) },
                {
                  label: "Outstanding",
                  value: money(fullyPaid ? 0 : detail?.remainingAmount),
                },
                {
                  label: "Payment completed within",
                  value:
                    fullyPaid && freeze?.paymentCompletedWithinDays != null
                      ? `${freeze.paymentCompletedWithinDays} day${
                          freeze.paymentCompletedWithinDays === 1 ? "" : "s"
                        }`
                      : null,
                  hide: !(
                    fullyPaid && freeze?.paymentCompletedWithinDays != null
                  ),
                },
                {
                  label: "Last payment date",
                  value: lastPayment
                    ? formatDate(lastPayment.paymentDate)
                    : null,
                  hide: !lastPayment,
                },
              ]}
            />

            <SubTitle>Repayment Schedule</SubTitle>
            {repayments.length ? (
              <table
                className="w-full mb-4 text-xs border border-gray-200"
                cellPadding="0"
                cellSpacing="0"
              >
                <thead>
                  <tr className="bg-gray-800 text-white">
                    <th className="px-2 py-2 text-left font-semibold">#</th>
                    <th className="px-2 py-2 text-left font-semibold">
                      Due date
                    </th>
                    <th className="px-2 py-2 text-left font-semibold">
                      Status
                    </th>
                    <th className="px-2 py-2 text-right font-semibold">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {repayments.map((item, index) => {
                    const isPaid = index < paidCount;
                    return (
                      <tr
                        key={item?._id || index}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-2 py-2 border-b border-gray-200 font-semibold text-gray-800">
                          Instalment {index + 1}
                        </td>
                        <td className="px-2 py-2 border-b border-gray-200 text-gray-700">
                          {formatDate(item?.paymentDate)}
                        </td>
                        <td className="px-2 py-2 border-b border-gray-200">
                          <span
                            className={`inline-block px-2 py-0.5 text-xs font-bold border ${
                              isPaid
                                ? "bg-green-50 text-green-800 border-green-200"
                                : "bg-yellow-50 text-yellow-800 border-yellow-200"
                            }`}
                          >
                            {isPaid ? "PAID" : "PENDING"}
                          </span>
                        </td>
                        <td className="px-2 py-2 border-b border-gray-200 text-right font-bold text-gray-800">
                          {money(item?.amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <EmptyNote>No repayments scheduled.</EmptyNote>
            )}

            <SubTitle>Made Payments</SubTitle>
            {madeRepayments.length ? (
              <table
                className="w-full mb-4 text-xs border border-gray-200"
                cellPadding="0"
                cellSpacing="0"
              >
                <thead>
                  <tr className="bg-gray-800 text-white">
                    <th className="px-2 py-2 text-left font-semibold">#</th>
                    <th className="px-2 py-2 text-left font-semibold">
                      Payment date
                    </th>
                    <th className="px-2 py-2 text-right font-semibold">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {madeRepayments.map((payment, index) => (
                    <tr
                      key={payment?._id || index}
                      className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      <td className="px-2 py-2 border-b border-gray-200 font-semibold text-gray-800">
                        Payment {index + 1}
                      </td>
                      <td className="px-2 py-2 border-b border-gray-200 text-gray-700">
                        {formatDate(payment?.paymentDate)}
                      </td>
                      <td className="px-2 py-2 border-b border-gray-200 text-right font-bold text-gray-800">
                        {money(payment?.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <EmptyNote>No payments made yet.</EmptyNote>
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );

  return ReactDOMServer.renderToStaticMarkup(content);
}
